import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateRelative } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Bell, Sparkles, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

export default function NotificationsBell() {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get(`/notifications?limit=20`);
            setItems(data.items);
            setUnread(data.unread_count);
        } catch {
            // silent — bell shouldn't crash on a 401 during transitions
        }
    }, []);

    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 60_000); // poll every minute
        return () => clearInterval(t);
    }, [refresh]);

    const markAllRead = async () => {
        await api.post(`/notifications/mark-read`, { notification_ids: null });
        refresh();
    };
    const dismiss = async (id, e) => {
        e.preventDefault();
        e.stopPropagation();
        await api.delete(`/notifications/${id}`);
        toast.success("Dismissed");
        refresh();
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="relative h-9 w-9 inline-flex items-center justify-center border border-border rounded-md bg-white hover:border-foreground/40 transition-colors"
                    data-testid="notifications-bell"
                >
                    <Bell className="w-4 h-4" strokeWidth={1.75} />
                    {unread > 0 && (
                        <span
                            className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-gold text-[10px] font-bold text-foreground font-mono"
                            data-testid="notifications-unread-count"
                        >
                            {unread > 9 ? "9+" : unread}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96 p-0" data-testid="notifications-popover">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <div className="font-display font-bold text-sm tracking-tight">Notifications</div>
                    {unread > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={markAllRead}
                            className="text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
                            data-testid="mark-all-read-btn"
                        >
                            Mark all read
                        </Button>
                    )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                    {items.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
                            No notifications yet.
                        </div>
                    ) : (
                        <ul>
                            <AnimatePresence>
                                {items.map((n) => (
                                    <motion.li
                                        key={n.id}
                                        layout
                                        initial={{ opacity: 0, y: -4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, x: 30 }}
                                        className={`group border-b border-border/60 last:border-0 ${n.is_read ? "" : "bg-gold/5"}`}
                                        data-testid={`notification-${n.id}`}
                                    >
                                        <NotificationItem n={n} onDismiss={(e) => dismiss(n.id, e)} onOpen={() => setOpen(false)} />
                                    </motion.li>
                                ))}
                            </AnimatePresence>
                        </ul>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

function NotificationItem({ n, onDismiss, onOpen }) {
    if (n.type === "mandate_match") {
        const p = n.payload || {};
        const top3 = p.top_3 || [];
        return (
            <Link
                to={`/deals/${n.deal_id}`}
                onClick={onOpen}
                className="block px-4 py-3 hover:bg-muted/40 transition-colors relative"
            >
                <div className="flex items-start gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-gold mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm">
                            <span className="font-medium">{p.match_count}</span> mandate-matched buyer{p.match_count === 1 ? "" : "s"} for{" "}
                            <span className="font-display font-bold">{p.deal_name}</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                            Top: {p.top_buyer_name} · score {p.top_score}
                            {top3.length > 1 && ` · ${top3.slice(1).map((m) => m.name).join(", ")}`}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mt-1">
                            {fmtDateRelative(n.created_at)}
                        </div>
                    </div>
                    <button
                        onClick={onDismiss}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                        data-testid={`dismiss-${n.id}`}
                        aria-label="Dismiss"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </Link>
        );
    }
    // Fallback for unknown types
    return (
        <div className="px-4 py-3 flex items-start gap-2">
            <Badge variant="outline" className="text-[10px] uppercase">{n.type}</Badge>
            <div className="text-xs text-muted-foreground flex-1">{JSON.stringify(n.payload)}</div>
        </div>
    );
}
